import { useState, useMemo, useCallback } from 'react';
import { format0 } from 'utils/number';
import { TrashCan, Edit, ChevronUp, ChevronDown } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { clearedFilters } from '../atoms';
import { FilterChip } from '../filter-chip';
import { fieldToChips, fieldParamKeys, type FieldChip } from './field-chips';
import type { TreeTableFilterField } from '../types';
import style from './filter-modal.module.scss';

interface FilterItem {
  label: string;
  key: string;
  /** Param keys to clear when removing this item */
  keysToRemove: string[];
  /** For picklist: specific value to splice from the array */
  arrayValueToRemove?: string;
}

interface FilterGroup {
  category: string;
  items: FilterItem[];
}

// Adapt a normalized chip to the preview's remove shape (keysToRemove). Ranges
// collapse into a single chip using the preview's raw / `format0` formatting.
const toFilterItems = (c: FieldChip): FilterItem[] => {
  switch (c.kind) {
    case 'labeled':
      return [{ label: c.label, key: c.key, keysToRemove: [c.param] }];
    case 'value':
      return [
        {
          label: c.resolved,
          key: c.key,
          keysToRemove: [c.param],
          arrayValueToRemove: c.rawValue,
        },
      ];
    case 'range': {
      const label =
        c.format === 'date'
          ? c.min && c.max
            ? `${c.fieldLabel}: ${c.min} \u2013 ${c.max}`
            : c.min
              ? `${c.fieldLabel} from ${c.min}`
              : `${c.fieldLabel} to ${c.max}`
          : c.min && c.max
            ? `${c.fieldLabel}: Between ${format0(
                Number(c.min)
              )} \u2013 ${format0(Number(c.max))}`
            : c.min
              ? `${c.fieldLabel} >= ${format0(Number(c.min))}`
              : `${c.fieldLabel} <= ${format0(Number(c.max))}`;
      return [
        {
          label,
          key: `${c.minParam}-${c.maxParam}`,
          keysToRemove: [c.minParam, c.maxParam],
        },
      ];
    }
    case 'value-range':
      return [
        {
          label: `${c.fieldLabel}: ${c.label}`,
          key: c.key,
          keysToRemove: [c.minParam, c.maxParam],
        },
      ];
  }
};

export const FilterPreview = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const [pending, setPending] = useAtom(atoms.pendingFiltersAtom);
  const modalFacets = useAtomValue(atoms.modalFacetsAtom);
  const committedFacets = useAtomValue(atoms.committedFacetsAtom);
  const refreshFacets = useSetAtom(atoms.refreshFacetsAtom);
  const setActiveCategory = useSetAtom(atoms.filterModalCategoryAtom);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Modal-scoped facet counts when live, else the last search's facets. Passed
  // to fieldToChips to resolve facet ids to their bucket names.
  const facets = modalFacets ?? committedFacets ?? undefined;

  const groups: FilterGroup[] = useMemo(() => {
    const result: FilterGroup[] = [];
    const categoryMap = new Map<string, FilterGroup>();

    const getGroup = (category: string): FilterGroup => {
      if (!categoryMap.has(category)) {
        const group: FilterGroup = { category, items: [] };
        categoryMap.set(category, group);
        result.push(group);
      }
      return categoryMap.get(category)!;
    };

    for (const field of config.filters) {
      for (const chip of fieldToChips(field, pending, facets).chips) {
        for (const item of toFilterItems(chip)) {
          getGroup(field.category).items.push(item);
        }
      }
    }

    return result.filter((g) => g.items.length > 0);
  }, [config.filters, pending, facets]);

  const handleRemoveItem = useCallback(
    (item: FilterItem) => {
      const next = { ...pending };
      if (item.arrayValueToRemove) {
        const arr = [
          ...((next[item.keysToRemove[0]] as string[] | undefined) ?? []),
        ];
        const idx = arr.indexOf(item.arrayValueToRemove);
        if (idx >= 0) arr.splice(idx, 1);
        next[item.keysToRemove[0]] = arr.length > 0 ? arr : undefined;

        // Removing a tree-table state must also drop that state's counties,
        // mirroring the tree's own state -> county cascade (toggleState).
        const treeField = config.filters.find(
          (f): f is TreeTableFilterField =>
            f.type === 'tree-table' && f.param === item.keysToRemove[0]
        );
        if (treeField) {
          // Only drop counties we can map to the removed state. With no county
          // facet loaded there's no county->state map, so leave counties as-is
          // — mirroring the tree's toggleState (which also no-ops here) rather
          // than wiping every staged county across all states.
          const buckets = facets?.[treeField.childFacetKey]?.buckets;
          if (buckets?.length) {
            const countyIds = new Set(
              buckets
                .filter((c) => c.stateId === item.arrayValueToRemove)
                .map((c) => c.id)
            );
            if (countyIds.size) {
              const counties = (
                (next[treeField.childParam] as string[] | undefined) ?? []
              ).filter((v) => !countyIds.has(v));
              next[treeField.childParam] =
                counties.length > 0 ? counties : undefined;
            }
          }
        }
      } else {
        for (const key of item.keysToRemove) {
          next[key] = undefined;
        }
      }
      setPending(next);
      refreshFacets(next);
    },
    [pending, setPending, refreshFacets, config.filters, facets]
  );

  const handleRemoveGroup = useCallback(
    (category: string) => {
      const next = { ...pending };
      const categoryFields = config.filters.filter(
        (f) => f.category === category
      );
      for (const field of categoryFields) {
        for (const key of fieldParamKeys(field)) next[key] = undefined;
      }
      setPending(next);
      refreshFacets(next);
    },
    [pending, setPending, config.filters, refreshFacets]
  );

  const handleEditGroup = useCallback(
    (category: string) => {
      setActiveCategory(category);
    },
    [setActiveCategory]
  );

  const toggleCollapse = useCallback((category: string) => {
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const handleClearAll = useCallback(() => {
    // Clear the filters but keep the spatial extent/AOI, text, and plot mode —
    // dropping plot_by_county here would desync the sidebar radio from results.
    const next = clearedFilters(config.defaultPayload, pending);
    setPending(next);
    refreshFacets(next);
  }, [config.defaultPayload, pending, setPending, refreshFacets]);

  return (
    <div>
      <div className={style.previewHeader}>
        <h6 className={style.previewTitle}>Selected Filters</h6>
        {groups.length > 0 && (
          <button
            type="button"
            className={style.previewClear}
            onClick={handleClearAll}
          >
            Clear
          </button>
        )}
      </div>
      {groups.length === 0 && (
        <p className={style.previewEmpty}>No filters selected</p>
      )}
      {groups.map((group) => {
        const isCollapsed = collapsed[group.category] ?? false;
        return (
          <div key={group.category} className={style.filterGroup}>
            <div className={style.groupHeader}>
              <span className={style.groupName}>{group.category}</span>
              <button
                type="button"
                aria-label={`Remove all ${group.category} filters`}
                onClick={() => handleRemoveGroup(group.category)}
                className={`${style.groupAction} ${style.groupActionDelete}`}
              >
                <TrashCan />
              </button>
              <button
                type="button"
                aria-label={`Edit ${group.category} filters`}
                onClick={() => handleEditGroup(group.category)}
                className={`${style.groupAction} ${style.groupActionEdit}`}
              >
                <Edit />
              </button>
              <button
                type="button"
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                onClick={() => toggleCollapse(group.category)}
                className={style.groupAction}
              >
                {isCollapsed ? <ChevronDown /> : <ChevronUp />}
              </button>
            </div>
            {!isCollapsed && (
              <div className={style.groupTags}>
                {group.items.map((item) => (
                  <FilterChip
                    key={item.key}
                    onClose={() => handleRemoveItem(item)}
                  >
                    {item.label}
                  </FilterChip>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
